import React, { useState } from 'react';
import { FiRepeat, FiCalendar } from 'react-icons/fi';

const ContinuousContractModal = ({ isOpen, onClose, onConfirm, instrument }) => {
  const [isContinuous, setIsContinuous] = useState(false);
  const [rolloverDays, setRolloverDays] = useState(3);
  
  if (!isOpen || !instrument) return null;
  
  const isFnO = ['FUTURE', 'OPTION', 'FUT', 'OPT'].includes(
    instrument.instrument_type?.toUpperCase()
  );
  
  // Format expiry_date to YYYY-MM-DD if present
  const formatDateForAPI = (dateValue) => {
    if (!dateValue) return null;
    // Handle ISO datetime string, Date object, or already formatted date string
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return null;
      return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD
    } catch {
      return null;
    }
  };
  
  const handleConfirm = () => {
    const formattedExpiry = formatDateForAPI(instrument.expiry_date);
    
    onConfirm({
      instrument,
      is_continuous: isContinuous,
      rollover_days_before_expiry: isContinuous ? rolloverDays : null,
      active_expiry_date: formattedExpiry
    });
    onClose();
    // Reset for next time
    setIsContinuous(false);
    setRolloverDays(3);
  };
  
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="bg-white dark:bg-bg-1 rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-[var(--divider)] bg-bg-3">
          <h3 className="ds-h3 text-fg-1 flex items-center gap-2">
            <FiRepeat className="text-indigo-600" />
            Add Feed: {instrument.symbol}
          </h3>
        </div>
        
        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Instrument Summary */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-2 py-1 text-xs rounded bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
              {instrument.exchange}
            </span>
            <span className="px-2 py-1 text-xs rounded bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
              {instrument.instrument_type}
            </span>
            {instrument.expiry_date && (
              <span className="px-2 py-1 text-xs rounded bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 flex items-center gap-1">
                <FiCalendar size={10} />
                Expiry: {new Date(instrument.expiry_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
              </span>
            )}
          </div>
          
          {/* Continuous Contract Option (F&O only) */}
          {isFnO && (
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-lg p-4 border border-indigo-200 dark:border-indigo-800">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isContinuous}
                  onChange={(e) => setIsContinuous(e.target.checked)}
                  className="mt-1 w-5 h-5 text-indigo-600 rounded border-[var(--divider)] focus:ring-indigo-500"
                />
                <div>
                  <span className="font-semibold text-fg-1">
                    Enable Continuous Contract Mode
                  </span>
                  <p className="text-sm text-fg-2 mt-1">
                    Automatically switch to the next month contract before expiry. 
                    Ideal for tracking futures continuously without manual intervention.
                  </p>
                </div>
              </label>
              
              {/* Rollover Days Selection */}
              {isContinuous && (
                <div className="mt-4 pl-8">
                  <label className="block text-sm font-medium text-fg-2 mb-2">
                    Rollover Days Before Expiry
                  </label>
                  <select
                    value={rolloverDays}
                    onChange={(e) => setRolloverDays(parseInt(e.target.value))}
                    className="ds-input w-full"
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(d => (
                      <option key={d} value={d}>{d} day{d > 1 ? 's' : ''} before expiry</option>
                    ))}
                  </select>
                  <p className="text-xs text-fg-2 mt-1">
                    Contract will switch {rolloverDays} day{rolloverDays > 1 ? 's' : ''} before the expiry date.
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Non-F&O Info */}
          {!isFnO && (
            <div className="bg-bg-3 rounded-lg p-4 border border-[var(--divider)]">
              <p className="text-sm text-fg-2">
                This instrument will be added for real-time monitoring. 
                Continuous contract mode is only available for Futures and Options.
              </p>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-[var(--divider)] bg-bg-3 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-fg-2 hover:bg-bg-3 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
          >
            Add Instrument
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContinuousContractModal;
